<ng-container *ngIf="comment$ | async; let comment">
  <div
    class="comment-header"
    [class.pb-0]="isCommentCollapsed"
    [class.toggle-collapse]="isCommentCollapsed && comment.$type !== CommentDraft_Type"
    (click)="collapse.next()"
  >
    <div class="grid">
      @if (author$ | async; as author) {
        <loop-contact-avatar
          style="grid-area: avatar; margin: var(--theme-spacing-half) 0"
          [contact]="author"
          [conversationId]="conversation?.cardId"
          [style.--custom-contact-avatar-size]="'30px'"
        />
      }

      <div
        class="flex flex-col gap-quarter padding-vertical-quarter padding-horizontal-half recipients-wrapper min-width-0"
        [class.all-visible]="areRecipientsFullyVisible"
        [class.selectable]="areRecipientsFullyVisible"
        (click)="toggleInformation($event)"
      >
        <div *ngIf="author$ | async; let author" class="author">
          <ng-container
            *ngTemplateOutlet="
              contactDataTpl;
              context: {
                $implicit: author | displayName,
                email: author.email,
                contact: author,
                replyTo: replyTo$ | async | displayName
              }
            "
          ></ng-container>
        </div>

        <div class="flex" [class.flex-col]="areRecipientsFullyVisible" [class.gap-quarter]="areRecipientsFullyVisible">
          <ng-container *ngIf="areRecipientsFullyVisible">
            <ng-container *ngIf="comment.replyTo?.resources | contactsInformation; let replyToRecipients">
              <ng-container
                *ngTemplateOutlet="
                  contactsInformationTpl;
                  context: { $implicit: 'Reply to', recipients: replyToRecipients }
                "
              ></ng-container>
            </ng-container>
          </ng-container>

          <ng-container *ngIf="comment.to?.resources | contactsInformation; let toRecipients">
            <ng-container
              *ngTemplateOutlet="contactsInformationTpl; context: { $implicit: 'To', recipients: toRecipients }"
            ></ng-container>
          </ng-container>

          <ng-container *ngIf="comment.cc?.resources | contactsInformation; let ccRecipients">
            <ng-container
              *ngTemplateOutlet="
                contactsInformationTpl;
                context: { $implicit: 'Cc', recipients: ccRecipients, isSpaceAtStart: true }
              "
            ></ng-container>
          </ng-container>

          <ng-container *ngIf="comment.bcc?.resources | contactsInformation; let bccRecipients">
            <ng-container
              *ngTemplateOutlet="
                contactsInformationTpl;
                context: { $implicit: 'Bcc', recipients: bccRecipients, isSpaceAtStart: true }
              "
            ></ng-container>
          </ng-container>
        </div>
      </div>

      <loop-icon
        *ngIf="hasAttachment$ | async"
        class="attachment-icon margin-horizontal-quarter"
        name="paperclip-solid"
        width="16px"
        height="16px"
      ></loop-icon>

      <time-ago
        [time]="comment.created"
        [isComment]="true"
        [loopHoverTooltip]="comment.created | fancyDate: DateFormat_RelativeNowOffsetShort"
        class="margin-horizontal-quarter"
      >
      </time-ago>

      <div
        *ngIf="
          areActionsVisibleSignal() &&
          comment.$type !== CommentDraft_Type &&
          conversation?.channelType !== ChannelType_LoopSpam
        "
        class="actions margin-horizontal-quarter"
      >
        <ng-container *ngIf="!isClosed">
          <loop-icon
            class="clickable-icon"
            name="reply-all"
            width="16px"
            height="16px"
            sleepClickEvent
            (click)="openQuickReply.emit(comment)"
          ></loop-icon>
        </ng-container>

        <loop-icon
          class="clickable-icon"
          name="arrow-up-right-from-square"
          width="16px"
          height="16px"
          sleepClickEvent
          [loopHoverTooltip]="'Open in new window'"
          (click)="popOut.next()"
        ></loop-icon>

        <loop-icon
          *browserExternalWindowHidden
          class="clickable-icon"
          name="ellipsis-vertical"
          width="16px"
          sleepClickEvent
          [loopClickDropdown]="(dropdownOptions$ | async) || []"
        />
      </div>
    </div>

    <ng-template
      #contactsInformationTpl
      let-contactIdentifier
      let-recipients="recipients"
      let-isSpaceAtStart="isSpaceAtStart"
    >
      <div *ngIf="!areRecipientsFullyVisible" class="identifier">
        <span *ngIf="isSpaceAtStart">&nbsp;</span>{{ contactIdentifier }}:&nbsp;
      </div>
      <div
        class="recipients text-overflow"
        [class.flex]="areRecipientsFullyVisible"
        [class.flex-wrap]="areRecipientsFullyVisible"
        *ngIf="recipients.length"
      >
        <ng-container *ngFor="let contact of recipients; let last = last; let first = first">
          <ng-container *ngIf="!areRecipientsFullyVisible; else fullRecipientsDataTpl">
            <ng-container
              *ngTemplateOutlet="
                simpleContactData;
                context: { $implicit: contact.displayName ?? contact.email, email: contact.emailt }
              "
            ></ng-container>
          </ng-container>
          <ng-template #fullRecipientsDataTpl>
            <div class="whitespace-no-wrap flex contact">
              <div *ngIf="areRecipientsFullyVisible && first" class="identifier">{{ contactIdentifier }}:&nbsp;</div>
              <ng-container
                *ngTemplateOutlet="
                  contactDataTpl;
                  context: {
                    $implicit: contact.displayName ?? contact.email,
                    email: contact.email,
                    contact: contact.contact
                  }
                "
              ></ng-container>
              <ng-container *ngIf="!last">,&nbsp;</ng-container>
              <ng-container *ngIf="last">&nbsp;</ng-container>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </ng-template>

    <ng-template #simpleContactData let-displayName let-email="email">
      {{ displayName ?? email }}
    </ng-template>

    <ng-template #contactDataTpl let-displayName let-email="email" let-contact="contact" let-replyTo="replyTo">
      <div class="whitespace-no-wrap flex" [class.selectable]="areRecipientsFullyVisible">
        {{ displayName }}
        <ng-container *ngIf="replyTo && !areRecipientsFullyVisible"
          ><span class="reply-to-contact">&nbsp;(Reply to: {{ replyTo }})</span></ng-container
        >
        <div [loopHoverTooltip]="areRecipientsFullyVisible ? contactHoverTpl : undefined">
          <ng-container *ngIf="email && displayName !== email && areRecipientsFullyVisible">
            <div class="email contents" [class.selectable]="areRecipientsFullyVisible">
              <span>&lt;{{ email }}&gt;</span>
            </div>
          </ng-container>
        </div>
      </div>

      <ng-template #contactHoverTpl>
        <loop-contact
          class="padding-vertical-half padding-horizontal flex-1"
          [contact]="contact"
          [conversationId]="conversation?.cardId"
          [showAvailabilityStatus]="false"
          [suffixTpl]="suffixTpl"
        ></loop-contact>
        <ng-template #suffixTpl>
          <div *ngIf="contact | isUser" class="flex items-center gap-half">
            <loop-icon
              class="clickable-icon"
              name="magnifying-glass"
              width="16px"
              height="16px"
              sleepClickEvent
              [loopHoverTooltip]="'Search emails'"
              (click)="searchEmails(contact)"
            ></loop-icon>
            <loop-copy-value
              [value]="contact.email"
              [notificationMessage]="'Email copied to clipboard'"
            ></loop-copy-value>
          </div>
        </ng-template>
      </ng-template>
    </ng-template>

    <div *ngIf="isCommentCollapsed" class="snippet-collapsed padding-horizontal-half padding-bottom-half">
      {{ comment.snippet.length === 256 ? comment.snippet + '...' : comment.snippet }}
    </div>
  </div>
</ng-container>
